import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { addDays, differenceInCalendarDays } from 'date-fns';
import { UserPlus } from 'lucide-react';
import { Banner } from '@shared/ui/feedback/Banner';
import { toast } from '@shared/ui/feedback/Toast';
import { Button } from '@shared/ui/primitives/Button';
import { toApiError } from '@shared/api/errors';
import { cn } from '@shared/lib/cn';
import { inviteCredentialsQueryKey } from '../InviteCredentialPass';
import { PICKER_ROW, PeoplePicker, joinFirstNames } from '../PeoplePicker';
import { PersonSheet } from '../PersonSheet';
import { ACCESS_INVITES_QUERY_KEY } from '../accessInviteCache';
import { createBooking, gatePinsQueryKey, type VisitorGroup } from '../memberAppApi';
import { MEMBER_CONTACTS_QUERY_KEY, type Person } from '../memberContactsApi';
import { firstName } from '../NewPinSheet';
import { displayPhone, isoDay, startOfToday } from '../visitorAccessFormat';
import { HELP, ICON_CHIP } from '../visitorAccessStyles';
import { VisitDates } from '../VisitDates';
import { BookingSteps, PropertyField, type BookingProps } from './BookingForm';

/**
 * A PIN for each saved guest that works every day of their stay; the gate texts it to them. Several
 * guests, ticked or from a group, each get their own PIN for the same stay.
 */
export function GuestStayBooking({ properties, onCreated, lead }: BookingProps): JSX.Element {
  // 1. Starts as one night from today for one adult, at the member's first property.
  const queryClient = useQueryClient();
  const [step, setStep] = useState(0);
  const [locationId, setLocationId] = useState(properties[0]!.location_id);
  const [ticked, setTicked] = useState<string[]>([]);
  const [group, setGroup] = useState<VisitorGroup>();
  const [guests, setGuests] = useState<Person[]>([]);
  const [noMobile, setNoMobile] = useState<Person>();
  const [arrives, setArrives] = useState(startOfToday);
  const [leaves, setLeaves] = useState(() => addDays(startOfToday(), 1));
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const many = guests.length > 1;

  // 2. A stay is at least one night and at most what the property allows. Another property can
  //    allow fewer, so the limit is checked as well as kept to by the calendar.
  const features = properties.find((property) => property.location_id === locationId)!.features;
  const maxNights = features.guest_stay_max_nights;
  const nights = differenceInCalendarDays(leaves, arrives);
  const nightsOk = nights >= 1 && nights <= maxNights;
  const nightsLimit = `A stay can be up to ${maxNights} ${maxNights === 1 ? 'night' : 'nights'}.`;

  // 3. Several guests must fit the property's group size, which is checked again if the property changes.
  const groupSize = features.guest_stay_groups ? { min: features.guest_stay_group_min, max: features.guest_stay_group_max } : null;
  const sizeOk = !many || (groupSize !== null && guests.length >= groupSize.min && guests.length <= groupSize.max);
  const sizeLimit = groupSize
    ? `Guest stay allows ${groupSize.min} to ${groupSize.max} people here. Go back to Guest to change who is staying.`
    : 'This property books one guest per stay. Go back to Guest to pick one.';

  // 4. A guest saved here with a mobile is ticked (with groups off, instead of whoever was); one still without is asked for it.
  const tick = (person: Person) => {
    setNoMobile(person.phone ? undefined : person);
    if (person.phone && !ticked.includes(person.member_contact_id))
      setTicked(groupSize ? ticked.concat(person.member_contact_id) : [person.member_contact_id]);
  };

  // 5. One PIN per guest, one after the other: each an adult, with any children on the first PIN made.
  //    One the gate refuses doesn't stop the rest; the member is told who has none.
  const mutation = useMutation({
    mutationFn: async () => {
      const created: Person[] = [];
      const failed: { guest: Person; message: string }[] = [];
      for (const guest of guests) {
        try {
          const { invite, credentials } = await createBooking({
            kind: 'guest_stay',
            member_contact_id: guest.member_contact_id,
            adults: many ? 1 : adults,
            children: created.length ? 0 : children,
            start_date: isoDay(arrives),
            end_date: isoDay(leaves),
            location_id: locationId,
          });
          if (credentials) queryClient.setQueryData(inviteCredentialsQueryKey(invite.access_invite_id), credentials);
          created.push(guest);
        } catch (error) {
          failed.push({ guest, message: toApiError(error).message });
        }
      }
      return { created, failed };
    },
    onSuccess: ({ created, failed }) => {
      void queryClient.invalidateQueries({ queryKey: ACCESS_INVITES_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: gatePinsQueryKey(locationId) });
      void queryClient.invalidateQueries({ queryKey: MEMBER_CONTACTS_QUERY_KEY });

      // A timed-out request may still have made the PIN, so the member looks before trying again.
      const failureText = failed.some(({ message }) => /timeout/i.test(message))
        ? 'The PIN may have been made. Check Active PINs before you try again.'
        : failed.map(({ guest, message }) => (many ? `${guest.display_name}: ${message}` : message)).join(' ');

      if (!created.length) {
        toast.danger(many ? "Couldn't create the PINs" : "Couldn't create the PIN", failureText);
        return;
      }
      toast.success(created.length === 1 ? 'PIN created' : `${created.length} PINs created`);
      if (failed.length) toast.danger(`No PIN for ${failed.map(({ guest }) => guest.display_name).join(', ')}`, failureText);
      onCreated();
    },
  });

  const steps = [
    {
      label: 'Guest',
      title: 'Who is staying?',
      description: 'The gate texts the PIN to each guest, so they need a mobile number.',
    },
    {
      label: 'Dates',
      title: 'When are they staying?',
      description: many
        ? `For ${joinFirstNames(guests)}. The gate texts each of them their own PIN.`
        : guests[0]
          ? `For ${guests[0].display_name}. The gate texts the PIN to ${displayPhone(guests[0].phone!)}.`
          : '',
    },
    { label: 'Party', title: 'How many are coming?', description: 'The PIN works every day of the stay.' },
  ];

  return (
    <BookingSteps
      lead={lead}
      steps={steps}
      step={step}
      onStep={setStep}
      action={
        step === 1 ? (
          <Button width="full" disabled={!nightsOk} onClick={() => setStep(2)}>
            Continue
          </Button>
        ) : step === 2 ? (
          <Button width="full" disabled={!nightsOk || !sizeOk} loading={mutation.isPending} onClick={() => mutation.mutate()}>
            {many ? `Create ${guests.length} PINs` : 'Create PIN'}
          </Button>
        ) : undefined
      }
    >
      {/* 6. Guests: groups, then everyone saved, to tick. One without a mobile is asked for it first. */}
      {step === 0 ? (
        <>
          {noMobile ? (
            <Banner
              variant="warning"
              title="No mobile number"
              description={`The gate texts the PIN to ${noMobile.display_name}. Add their mobile number under Edit details.`}
              action={
                <Button size="sm" variant="secondary" onClick={() => setEditOpen(true)}>
                  Edit details
                </Button>
              }
            />
          ) : null}
          <PeoplePicker
            allowed={() => true}
            blockedReason={(person) => (person.phone ? undefined : 'No mobile saved')}
            groupSize={groupSize}
            typeName="Guest stay"
            ticked={ticked}
            onTick={(ids) => {
              setNoMobile(undefined);
              setTicked(ids);
            }}
            group={group}
            onGroup={setGroup}
            onBlocked={setNoMobile}
            onContinue={(picked) => {
              setGuests(picked);
              setStep(1);
            }}
            emptyText="Add the people staying first."
          >
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              className={cn(PICKER_ROW, 'border-dashed text-primary-500')}
            >
              <span className={ICON_CHIP}>
                <UserPlus className="h-4.5 w-4.5" strokeWidth={2} />
              </span>
              <span className="text-[15px] font-semibold">Add a person</span>
            </button>
          </PeoplePicker>
        </>
      ) : null}

      {/* 7. Dates: the day they arrive and how many nights, within the nights the property allows. */}
      {step === 1 ? (
        <>
          <VisitDates
            dayLabel="Arrives"
            start={arrives}
            onStart={(day) => {
              setArrives(day);
              setLeaves(addDays(day, nights));
            }}
            length={{ unit: 'night', value: nights, max: maxNights, onChange: (value) => setLeaves(addDays(arrives, value)) }}
          />
          {!nightsOk ? <p className={cn(HELP, 'text-danger')}>{nightsLimit}</p> : null}
        </>
      ) : null}

      {/* 8. Party: one adult per guest when there are several, then the property, whose limits are shown if the stay breaks them. */}
      {step === 2 ? (
        <>
          <div className="flex flex-col rounded-[14px] border border-hairline px-3.5 py-1">
            {many ? (
              <div className="flex min-h-[60px] items-center justify-between border-b border-hairline">
                <span className="text-[15px] font-semibold">Adults</span>
                <span className="text-[13px] text-ink-secondary">{guests.length}, one PIN each</span>
              </div>
            ) : (
              <Count label="Adults" value={adults} min={1} onChange={setAdults} />
            )}
            <Count label="Children" value={children} min={0} onChange={setChildren} />
          </div>
          {many ? <p className={HELP}>Children come in on {firstName(guests[0]!.display_name)}'s PIN.</p> : null}
          <PropertyField properties={properties} value={locationId} onChange={setLocationId} />
          {!nightsOk ? <p className={cn(HELP, 'text-danger')}>{nightsLimit} Go back to Dates to change the stay.</p> : null}
          {!sizeOk ? <p className={cn(HELP, 'text-danger')}>{sizeLimit}</p> : null}
        </>
      ) : null}

      <PersonSheet
        open={addOpen}
        onOpenChange={setAddOpen}
        detailsOnly
        onSaved={(person) => {
          setAddOpen(false);
          tick(person);
        }}
      />
      {noMobile ? (
        <PersonSheet
          open={editOpen}
          onOpenChange={setEditOpen}
          person={noMobile}
          onSaved={(person) => {
            setEditOpen(false);
            tick(person);
          }}
        />
      ) : null}
    </BookingSteps>
  );
}

/** One count in the party: its name, then minus, the number and plus in round buttons. */
function Count({ label, value, min, onChange }: { label: string; value: number; min: number; onChange: (value: number) => void }): JSX.Element {
  const step = 'flex h-10 w-10 items-center justify-center rounded-full border border-hairline bg-card text-xl text-ink disabled:opacity-40';
  const name = label.toLowerCase();

  return (
    <div className="flex min-h-[60px] items-center justify-between border-b border-hairline last:border-b-0">
      <span className="text-[15px] font-semibold">{label}</span>
      <span className="flex items-center gap-3.5">
        <button type="button" aria-label={`Fewer ${name}`} className={step} disabled={value <= min} onClick={() => onChange(value - 1)}>
          −
        </button>
        <span className="min-w-4 text-center text-base font-semibold">{value}</span>
        <button type="button" aria-label={`More ${name}`} className={step} disabled={value >= 99} onClick={() => onChange(value + 1)}>
          +
        </button>
      </span>
    </div>
  );
}
